import React, { useContext, useState } from 'react'
import Utils from 'Common/Utils';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import ServiceWrapper from 'Services/ServiceWrapper';
import AppSettings from 'Settings/AppSettings';
import AuthManager from 'Common/AuthManager';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Button from 'Components/DesignSystem/Button/Button';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Textarea from 'Components/DesignSystem/Textarea/Textarea';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Cross } from 'Components/DesignSystem/Iconography/Icons';
import { AppContext } from 'Context/AppContext';

interface CreateCategoryTypeFormData {
    name: string;
    description: string;
    industry_id: string;
    id: string;
    active: boolean;
}

interface CreateCategoryTypeModalProps {
    isOpen: boolean;
    onClose: (createdTypeId?: string) => void
}

const CreateCategoryTypeModal: React.FC<CreateCategoryTypeModalProps> = ({
    isOpen,
    onClose
}) => {
    const appContext = useContext(AppContext);
    const industryId = appContext?.get_selected_industry() && appContext?.get_selected_industry()?.id;
    const initialData = {
        name: '',
        id: '',
        description: '',
        active: true,
        industry_id: industryId || ''
    };
    const [formData, setFormData] = useState<CreateCategoryTypeFormData>(initialData);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const validationOptions = {
        name: {
            required: true,
            minLength: 2,
            maxLength: 60,
            type: "alnum_with_spaces",
        }
    };

    const isFormValid = (): boolean => {
        const validation = Utils.validate(formData, validationOptions);
        return Boolean(validation);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev: CreateCategoryTypeFormData) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isFormValid()) {
            toast.error('Please fill in all required fields correctly');
            return;
        }

        setIsLoading(true);

        try {
            const response = await ServiceWrapper.doPost(
                `${AppSettings.GMS_BACKEND_API_URL}categorytype/create`,
                formData,
                null,
                AuthManager.getGmsToken()
            );

            if (!response.errorfound && response.data.done) {
                toast.success(response.data.message);
                // Backend (Entities/category_types.py::create) returns
                // {done, message, id} at the top level of the JSON body, which
                // ServiceWrapper.doPost then places in response.data — so the
                // new type's UID lives at response.data.id. The old
                // response.data.data?.id path was never set, which is why the
                // newly-created category type wasn't auto-selected in the
                // Create Category modal's type dropdown.
                const createdId = response.data?.id || '';
                setFormData(initialData);
                onClose(createdId);
            } else {
                toast.error(response.data.message || 'Failed to create category type');
            }
        } catch (error) {
            toast.error('Error creating category type');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    /*
        DS SidePanel (2026-09-05), same shape as CreateCategoryModal — including
        its own panel testid at last (the old outer div copy-pasted
        `create-category-modal`, a duplicate whenever both panels stacked).
        The name field keeps the native `required` — per okf/mapping/e2e.md
        § 7.1 it is the only real client-side block; isFormValid() is a pinned
        always-true. This panel stacks on top of CreateCategoryModal;
        useDismissable's LIFO stack means Escape closes THIS layer first.
    */
    return (
        <SidePanel
            isOpen
            onClose={() => onClose()}
            width="lg"
            title="Create Category Type"
            tabs={[]}
            ariaLabel="Create Category Type"
            data-testid="create-category-type-modal"
            headerActions={
                <IconButton
                    size="sm"
                    aria-label="Close"
                    id="create-category-type-modal-close-btn"
                    data-testid="components-createcategorytypemodal-div-3"
                    onClick={() => onClose()}
                >
                    <Cross size={16} />
                </IconButton>
            }
        >
            <form onSubmit={handleSubmit} className="tw-space-y-6">
                {/* Name Field */}
                <div>
                    <TextField
                        label="Name"
                        required
                        // TODO(design-system): TextField maps `required` to
                        // aria-required ONLY — this form's only real empty-name
                        // block IS native validation (okf/mapping/e2e.md § 7.1),
                        // so the attribute is restored through the forwarded ref.
                        ref={(el) => { if (el) el.required = true; }}
                        id="type_name"
                        name="name"
                        value={formData.name}
                        onChange={(_value, e) => e && handleInputChange(e)}
                        placeholder="Enter Category Type Name"
                        maxLength={60}
                        error={formData.name.length < 2}
                        data-testid="components-createcategorytypemodal-input-1"
                    />
                    <Typography variant="caption-metadata" as="div" className="tw-text-ink-tertiary tw-mt-1">
                        Name must be 2-60 characters long, contain letters, numbers and spaces, and must not contain special characters.
                    </Typography>
                </div>

                {/* Description Field */}
                <Textarea
                    label="Description"
                    id="type_description"
                    name="description"
                    rows={5}
                    value={formData.description}
                    onChange={(_value, e) => e && handleInputChange(e)}
                    placeholder="Enter description"
                    data-testid="components-createcategorytypemodal-textarea-2"
                />

                {/* Footer Buttons */}
                <div className="tw-flex tw-gap-3 tw-justify-start">
                    <Button
                        variant="secondary"
                        size="sm"
                        type="button"
                        data-testid="create-category-type-cancel-btn"
                        onClick={() => onClose()}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        type="submit"
                        data-testid="create-category-type-submit-btn"
                        disabled={!isFormValid() || isLoading}
                        loading={isLoading}
                    >
                        {isLoading ? 'Creating...' : 'Done'}
                    </Button>
                </div>
            </form>
        </SidePanel>
    );
};

export default CreateCategoryTypeModal